// Esportazione CSV di qualunque tabella presente nella pagina (2026-10-06, richiesta: "rendi
// esportabile qualsiasi tabella/elenco"). Lavora sul DOM, non sui dati: esporta ciò che
// l'utente vede — colonne visibili nell'ordine scelto, righe caricate, ordinamento e filtri
// correnti — e vale per ogni tabella presente e futura senza codice per pagina. Limite
// dichiarato: le griglie che mostrano solo le prime N righe ("Show all") esportano quelle.

export interface ExportableTable {
  element: HTMLTableElement;
  name: string;
  rows: number;
}

// Testo di un elemento come lo legge una persona: i nodi di testo uno dopo l'altro separati da
// uno spazio (innerText incolla fra loro badge e testo inline: "minutes.×6"), senza gli
// elementi nascosti. textContent e non innerText anche perché quest'ultimo applica il CSS
// text-transform delle intestazioni (restituirebbe "RULE" per "Rule").
function readableText(root: Element): string {
  const parts: string[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node => {
      const parent = (node as Text).parentElement;
      if (!parent || parent.closest('select, option, script, style')) return NodeFilter.FILTER_REJECT;
      // Solo ciò che è davvero a schermo: i tooltip al passaggio del mouse (es. IP e MAC
      // dell'asset) stanno nel DOM ma non si vedono, e non vanno nel file.
      if (parent.closest('[hidden], [aria-hidden="true"]')) return NodeFilter.FILTER_REJECT;
      const visible = typeof parent.checkVisibility === 'function'
        ? parent.checkVisibility({ visibilityProperty: true, opacityProperty: true } as CheckVisibilityOptions)
        : getComputedStyle(parent).display !== 'none';
      return visible ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) parts.push(text);
  }
  return parts.join(' ');
}

// Testo di una cella: select → opzione scelta, input → valore, checkbox → vuoto, il resto →
// testo leggibile.
function cellText(cell: HTMLTableCellElement): string {
  const select = cell.querySelector('select');
  if (select) return select.options[select.selectedIndex]?.text?.trim() ?? '';
  const input = cell.querySelector('input');
  if (input) return input.type === 'checkbox' ? (input.checked ? 'x' : '') : input.value;
  return readableText(cell);
}

function headerText(cell: HTMLTableCellElement): string {
  return readableText(cell);
}

export function tableToRows(table: HTMLTableElement): string[][] {
  const headerRow = table.tHead?.rows[0];
  const headers = headerRow ? [...headerRow.cells].map(headerText) : [];
  const out: string[][] = [];
  const bodies = table.tBodies.length ? [...table.tBodies] : [];
  for (const body of bodies) {
    for (const tr of [...body.rows]) {
      const cells = [...tr.cells];
      // Righe di servizio ("Loading…", "No result", dettaglio espanso): una sola cella estesa.
      if (cells.length === 1 && (cells[0].colSpan > 1 || headers.length > 1)) continue;
      out.push(cells.map(cellText));
    }
  }
  // Colonne senza intestazione (checkbox, azioni, pannello colonne) non si esportano se sono
  // vuote in tutte le righe.
  const keep = headers.map((h, i) => h !== '' || out.some(r => (r[i] ?? '') !== ''));
  const filter = (row: string[]) => row.filter((_, i) => keep[i] !== false);
  return [filter(headers), ...out.map(filter)];
}

// Separatore ";" e BOM UTF-8: è ciò che Excel in italiano apre direttamente in colonne con
// gli accenti giusti; "," finirebbe tutto in una cella.
export function rowsToCsv(rows: string[][]): string {
  const escape = (v: string) => (/[;"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return '﻿' + rows.map(r => r.map(escape).join(';')).join('\r\n') + '\r\n';
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'table';
}

// Nome leggibile della tabella: `data-export-name` se la pagina lo dichiara, altrimenti il
// titolo della card che la contiene (h1/h2/h3 più vicino sopra), altrimenti il titolo pagina.
function tableName(table: HTMLTableElement, index: number): string {
  const declared = table.getAttribute('data-export-name') || table.getAttribute('aria-label');
  if (declared) return declared;
  let node: HTMLElement | null = table;
  while (node && node.tagName !== 'MAIN') {
    const container: HTMLElement | null = node.parentElement;
    if (!container) break;
    for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
      const heading = sibling.matches('h1,h2,h3') ? sibling : sibling.querySelector('h1,h2,h3');
      if (heading?.textContent?.trim()) return heading.textContent.replace(/\s+/g, ' ').trim();
    }
    node = container;
  }
  return `Table ${index + 1}`;
}

export function findExportableTables(root: ParentNode = document): ExportableTable[] {
  const scope = (root as Document).querySelector?.('main') || root;
  return [...scope.querySelectorAll('table')]
    .map((element, index) => ({ element, name: tableName(element, index), rows: tableToRows(element).length - 1 }))
    .filter(t => t.rows > 0);
}

export function exportTable(table: ExportableTable, pageTitle: string): void {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  const page = slug(pageTitle);
  const name = slug(table.name);
  const base = name === page || page.startsWith(name) ? page : `${page}-${name}`;
  downloadCsv(`${base}-${stamp}.csv`, rowsToCsv(tableToRows(table.element)));
}

// Più tabelle con le stesse colonne (es. Log Sources: una tabella per broker) in un solo CSV,
// con il nome della tabella come prima colonna. undefined se le intestazioni non coincidono.
export function mergeTables(tables: ExportableTable[]): string[][] | undefined {
  if (tables.length < 2) return undefined;
  const parsed = tables.map(t => tableToRows(t.element));
  const header = parsed[0][0].join('\u0000');
  if (!parsed.every(rows => rows[0].join('\u0000') === header)) return undefined;
  const out: string[][] = [['Table', ...parsed[0][0]]];
  parsed.forEach((rows, i) => { for (const row of rows.slice(1)) out.push([tables[i].name, ...row]); });
  return out;
}

export function exportMerged(tables: ExportableTable[], pageTitle: string): boolean {
  const rows = mergeTables(tables);
  if (!rows) return false;
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  downloadCsv(`${slug(pageTitle)}-all-${stamp}.csv`, rowsToCsv(rows));
  return true;
}
